/**
 * Дни и недели для фильтров по дате. Даты — строки `ГГГГ-ММ-ДД` по часам человека; арифметика идёт в UTC над
 * календарными числами, поэтому переход на летнее время и пояс браузера день не сдвигают.
 */

export const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const

const DAY = /^\d{4}-\d{2}-\d{2}$/

export const isIsoDay = (value: string): boolean => DAY.test(value)

function parts(day: string): { year: number; month: number; date: number } {
  return { year: Number(day.slice(0, 4)), month: Number(day.slice(5, 7)), date: Number(day.slice(8, 10)) }
}

const utc = (day: string): Date => {
  const { year, month, date } = parts(day)
  return new Date(Date.UTC(year, month - 1, date))
}

/** Календарный день по часам браузера. */
export function isoDayOf(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function addDays(day: string, count: number): string {
  const moved = utc(day)
  moved.setUTCDate(moved.getUTCDate() + count)
  return `${moved.getUTCFullYear()}-${String(moved.getUTCMonth() + 1).padStart(2, '0')}-${String(moved.getUTCDate()).padStart(2, '0')}`
}

/** Номер дня недели с понедельника: 0 — Пн … 6 — Вс. */
export function weekdayIndex(day: string): number {
  return (utc(day).getUTCDay() + 6) % 7
}

/** Семь дней текущей недели с понедельника по воскресенье; прошлая неделя не показывается. */
export function weekOf(today: string): readonly string[] {
  const monday = addDays(today, -weekdayIndex(today))
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index))
}

const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'] as const

/** Первый день месяца дня `day`. */
export const firstOfMonth = (day: string): string => `${day.slice(0, 7)}-01`

/** Последний день месяца дня `day`. */
export const lastOfMonth = (day: string): string => addDays(firstOfMonth(addDays(firstOfMonth(day), 31)), -1)

/** Первое января года дня `day`. */
export const firstOfYear = (day: string): string => `${day.slice(0, 4)}-01-01`

/** Начало «всего времени»: раньше этого дня чеков не бывает, а запрос получает честную дату, а не пустое значение. */
export const ALL_TIME_FROM = '2000-01-01'

const MS_PER_DAY = 86_400_000

/** Сколько дней в периоде включительно. */
export function spanDays(from: string, to: string): number {
  return Math.round((utc(to).getTime() - utc(from).getTime()) / MS_PER_DAY) + 1
}

const MONTHS_FULL = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'] as const

/** «Октябрь 2026» по первому дню месяца или любому его дню. */
export function formatMonth(day: string): string {
  const { year, month } = parts(day)
  const name = MONTHS_FULL[month - 1] ?? ''
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`
}

/** «Янв», «Окт»: короткое имя месяца для кнопки. */
export function formatMonthShort(day: string): string {
  const name = MONTHS_SHORT[parts(day).month - 1] ?? ''
  return `${name.charAt(0).toUpperCase()}${name.slice(1)}`
}

/** Все двенадцать месяцев года дня `today` — первые дни, с января по декабрь. */
export function monthsOfYearAll(today: string): readonly string[] {
  return Array.from({ length: 12 }, (_, index) => `${today.slice(0, 4)}-${String(index + 1).padStart(2, '0')}-01`)
}

/** Месяцы текущего года с января по текущий включительно — первые дни, от нового к старому. */
export function monthsOfYear(today: string): readonly string[] {
  const { month } = parts(today)
  return Array.from({ length: month }, (_, index) => `${today.slice(0, 4)}-${String(month - index).padStart(2, '0')}-01`)
}

/**
 * Режет период на куски по календарным месяцам, каждый не длиннее `maxDays`: источник, который отдаёт за раз не больше N дней,
 * читается кусками и склеивается. Куски идут от старых к новым; пустой или перевёрнутый период — без кусков.
 */
export function chunkRange(from: string, to: string, maxDays: number): readonly { from: string; to: string }[] {
  const chunks: { from: string; to: string }[] = []
  for (let start = from; start <= to; ) {
    let end = lastOfMonth(start)
    if (end > to) end = to
    while (spanDays(start, end) > maxDays) end = addDays(end, -1)
    chunks.push({ from: start, to: end })
    start = addDays(end, 1)
  }
  return chunks
}

/** «15 окт». */
export function formatDayShort(day: string): string {
  const { month, date } = parts(day)
  return `${date} ${MONTHS_SHORT[month - 1] ?? ''}`.trim()
}

/** «15.10.2026». */
export function formatDayFull(day: string): string {
  const { year, month, date } = parts(day)
  return `${String(date).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`
}
